/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { ColumnInstance } from 'react-table';

import { DateTime } from 'luxon';
import { NotaryAPI } from '../../../api/notary/types';
import { ActionCell } from './ActionCell';
import { TableHeader } from './TableHeader';
import { TableCell } from './TableCell';
import { SignerCell } from './SignerCell';
import { IconCell } from './IconCell';
import { StatusCell } from './StatusCell';
import { FilterParams } from '@shared/types/pipeline';
import { formatIsoDate, titleCase } from '@utils/formatting';
import { getAppointmentTimeValues, getNotaryTaskStatus } from '@utils/pipelineUtils';

interface BaseColumnConfig {
  Cell: React.FC<{ value: string }>;
  Header: React.FC<{ column: ColumnInstance<any> }>;
}

const baseColumnConfig: BaseColumnConfig = {
  Cell: TableCell,
  Header: TableHeader,
};

export const getColumnConfigurations = (
  fetchRooms: (filters: FilterParams) => Promise<void>,
  filters: FilterParams,
) => [
  {
    ...baseColumnConfig,
    accessor: (room: NotaryAPI.RoomListItem) => room.status,
    Cell: StatusCell,
    id: ' ',
    width: '3%',
  },
  {
    ...baseColumnConfig,
    accessor: (room: NotaryAPI.RoomListItem) => formatIsoDate(room.scheduleDate),
    id: 'Closing Date',
    width: '8%',
  },
  {
    ...baseColumnConfig,
    accessor: (room: NotaryAPI.RoomListItem) => room.state,
    id: 'State',
    width: '3%',
  },
  {
    ...baseColumnConfig,
    accessor: (room: NotaryAPI.RoomListItem) => titleCase(room.externalOrganization),
    id: 'Organization',
    width: '8%',
  },
  {
    ...baseColumnConfig,
    accessor: (room: NotaryAPI.RoomListItem) => room.signers,
    Cell: SignerCell,
    id: 'Signer(s)',
    width: '10%',
  },
  {
    ...baseColumnConfig,
    accessor: (room: NotaryAPI.RoomListItem) => {
      return { roomId: room.id, notaryName: room.notaryName, roomStatus: room.status };
    },
    Cell: ({ value }: { value: { roomId: string; notaryName: string | null; roomStatus: string } }) => (
      <ActionCell value={value} fetchRooms={() => fetchRooms(filters)} />
    ),
    id: 'Assigned Notary',
    width: '8%',
  },
  {
    ...baseColumnConfig,
    accessor: (room: NotaryAPI.RoomListItem) => {
      return getAppointmentTimeValues(room.preferredTimeSlots, room.scheduleTime);
    },
    Cell: IconCell,
    id: `Appointment Time (${DateTime.fromISO(new Date().toISOString()).toFormat(`ZZZZ`)})`,
    width: '10%',
  },
  {
    ...baseColumnConfig,
    accessor: (room: NotaryAPI.RoomListItem) => getNotaryTaskStatus('Lender Documents', room.notaryTasks),
    Cell: IconCell,
    id: 'Lender Documents',
    width: '10%',
  },
  {
    ...baseColumnConfig,
    accessor: (room: NotaryAPI.RoomListItem) => getNotaryTaskStatus('Title Documents', room.notaryTasks),
    Cell: IconCell,
    id: 'Title Documents',
    width: '10%',
  },
];
